import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { api } from 'constants/constant';
import { clearCachedUserMetadata } from 'auth/auth';

const isBrowser = () => typeof window !== 'undefined';
let refreshPromise = null;

export const buildBaseQueryWithReauth = ({ baseUrl = api, prepareHeaders: customPrepareHeaders } = {}) => {
  // Inject Bearer token from sessionStorage for all sessions.
  // Every tab stores its own token in sessionStorage after login so it
  // doesn't depend on the shared cookie (which can be overwritten by
  // another tab on a different subdomain).
  const defaultPrepareHeaders = (headers) => {
    const hasTabToken = typeof sessionStorage !== 'undefined' &&
      !!sessionStorage.getItem('fm_access_token');
    if (hasTabToken) {
      const token = sessionStorage.getItem('fm_access_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }
    if (customPrepareHeaders) {
      return customPrepareHeaders(headers);
    }
    return headers;
  };

  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials: 'include',
    prepareHeaders: defaultPrepareHeaders,
  });
  const refreshBaseQuery = fetchBaseQuery({
    baseUrl: api,
    credentials: 'include',
    prepareHeaders: defaultPrepareHeaders,
  });

  return async (args, apiContext, extraOptions) => {
    let result = await rawBaseQuery(args, apiContext, extraOptions);

    if (
      result?.error &&
      (result.error.status === 401 || result.error.status === 403)
    ) {
      // eslint-disable-next-line no-console
      console.warn('[RTK Reauth] triggering refresh due to', {
        status: result.error.status,
        url: typeof args === 'string' ? args : args?.url,
      });
      const isVisible = !isBrowser() || document.visibilityState === 'visible';
      if (isVisible) {
        if (!refreshPromise) {
          // For secondary sessions, send refresh_token in body instead of relying on cookie.
          const isSecondary = typeof sessionStorage !== 'undefined' &&
            !!sessionStorage.getItem('fm_access_token');
          const refreshArgs = isSecondary
            ? { url: 'auth/session/refresh', method: 'POST', body: { refresh_token: sessionStorage.getItem('fm_refresh_token') } }
            : { url: 'auth/session/refresh', method: 'POST' };

          refreshPromise = refreshBaseQuery(
            refreshArgs,
            apiContext,
            extraOptions
          ).then((refreshResult) => {
            // For secondary sessions, update sessionStorage with new tokens from body.
            if (!refreshResult.error && isSecondary && refreshResult.data) {
              if (refreshResult.data.access_token) {
                sessionStorage.setItem('fm_access_token', refreshResult.data.access_token);
              }
              if (refreshResult.data.refresh_token) {
                sessionStorage.setItem('fm_refresh_token', refreshResult.data.refresh_token);
              }
            }
            return refreshResult;
          }).finally(() => {
            refreshPromise = null;
          });
        }

        const refreshResult = await refreshPromise;

        if (!refreshResult.error) {
          result = await rawBaseQuery(args, apiContext, extraOptions);
          return result;
        }
      }

      if (isVisible) {
        clearCachedUserMetadata();
        if (isBrowser()) {
          window.location.href = `${window.location.origin}/session-timeout`;
        }
      }
    }

    return result;
  };
};

export const baseQueryWithReauth = buildBaseQueryWithReauth({ baseUrl: api });
